import { useState, useEffect, useMemo } from 'react';
import { useAppStore } from '../stores/appStore';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
  Badge,
} from '../components/ui';
import {
  Settings as SettingsIcon,
  Key,
  CreditCard,
  Bell,
  Save,
  ExternalLink,
  Target,
  CheckCircle,
  Trash2,
  Plus,
} from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { api } from '../lib/api';
import { toast } from '../stores/toastStore';
import { isPartnerReadOnlyMode } from '../lib/readOnlyMode';
import { markLanguageAsUserSelected } from '../i18n';
import { useTranslation } from 'react-i18next';

type SettingsTab = 'general' | 'api' | 'billing' | 'notifications' | 'marketing';

export function Settings() {
  const { tenant, setTenant } = useAppStore();
  const { i18n, t } = useTranslation();
  const isReadOnlyMode = isPartnerReadOnlyMode();
  const [activeTab, setActiveTab] = useState<SettingsTab>('general');
  const [isSaving, setIsSaving] = useState(false);
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);

  // Form states — OpenAI
  const [openaiKey, setOpenaiKey] = useState('');
  const [openaiKeyStatus, setOpenaiKeyStatus] = useState<{ configured: boolean; maskedKey?: string }>({ configured: false });
  const [isSavingKey, setIsSavingKey] = useState(false);
  const [isDeletingKey, setIsDeletingKey] = useState(false);

  // Form states — Gemini
  const [geminiKey, setGeminiKey] = useState('');
  const [geminiKeyStatus, setGeminiKeyStatus] = useState<{ configured: boolean; maskedKey?: string }>({ configured: false });
  const [isSavingGeminiKey, setIsSavingGeminiKey] = useState(false);
  const [isDeletingGeminiKey, setIsDeletingGeminiKey] = useState(false);

  // Form states — Claude (Anthropic)
  const [anthropicKey, setAnthropicKey] = useState('');
  const [anthropicKeyStatus, setAnthropicKeyStatus] = useState<{ configured: boolean; maskedKey?: string }>({ configured: false });
  const [isSavingAnthropicKey, setIsSavingAnthropicKey] = useState(false);
  const [isDeletingAnthropicKey, setIsDeletingAnthropicKey] = useState(false);
  const [technicalEmail, setTechnicalEmail] = useState('');
  const [isTechnicalEmailDirty, setIsTechnicalEmailDirty] = useState(false);
  const [partnerCode, setPartnerCode] = useState('');
  const [isActivatingPartner, setIsActivatingPartner] = useState(false);

  const [bitrixFields, setBitrixFields] = useState<any>(null);
  
  // Notification states
  const [notifications, setNotifications] = useState({
    errorAlerts: tenant?.settings?.notifications?.errorAlerts ?? true,
    connectionLost: tenant?.settings?.notifications?.connectionLost ?? true,
    weeklyReport: tenant?.settings?.notifications?.weeklyReport ?? true,
  });

  // Marketing states
  const [marketingSettings, setMarketingSettings] = useState({
    captureEnabled: tenant?.settings?.marketingCapture?.captureEnabled ?? false,
    matchMode: tenant?.settings?.marketingCapture?.matchMode || 'contains',
    targetEntity: tenant?.settings?.marketingCapture?.targetEntity || 'lead',
    dealPipelineId: tenant?.settings?.marketingCapture?.dealPipelineId || 'all',
    rules: Array.isArray(tenant?.settings?.marketingCapture?.rules)
      ? tenant?.settings?.marketingCapture?.rules
      : [
          {
            id: crypto.randomUUID(),
            code: '',
            targetEntity: 'lead',
            dealPipelineId: 'all',
            utmSource: '',
            utmMedium: '',
            utmCampaign: '',
            utmContent: '',
            utmTerm: '',
          },
        ],
  });

  // Initialize technical email when tenant changes
  useEffect(() => {
    if (tenant) {
      setTechnicalEmail(tenant.settings?.technicalResponsibleEmail || tenant.email || '');
      setIsTechnicalEmailDirty(false);
    }
  }, [tenant?.id]);

  // Keep technical email synced with backend updates when user is not editing
  useEffect(() => {
    if (!tenant || isTechnicalEmailDirty) {
      return;
    }

    setTechnicalEmail(tenant.settings?.technicalResponsibleEmail || tenant.email || '');
  }, [tenant?.settings?.technicalResponsibleEmail, tenant?.email, isTechnicalEmailDirty]);

  // Update remaining states when tenant data is loaded
  useEffect(() => {

    if (tenant?.settings) {
      if (tenant.settings.notifications) {
        setNotifications({
            errorAlerts: tenant.settings.notifications.errorAlerts ?? true,
            connectionLost: tenant.settings.notifications.connectionLost ?? true,
            weeklyReport: tenant.settings.notifications.weeklyReport ?? true,
        });
      }
      if (tenant.settings.marketingCapture) {
        const legacyTargetEntity = tenant.settings.marketingCapture.targetEntity || 'lead';
        const legacyDealPipelineId = tenant.settings.marketingCapture.dealPipelineId || 'all';
        const normalizedRules = Array.isArray(tenant.settings.marketingCapture.rules)
          ? tenant.settings.marketingCapture.rules.map((rule: any) => ({
              ...rule,
              targetEntity: rule?.targetEntity === 'deal' ? 'deal' : (legacyTargetEntity === 'deal' ? 'deal' : 'lead'),
              dealPipelineId: String(rule?.dealPipelineId || legacyDealPipelineId || 'all'),
            }))
          : [];

        setMarketingSettings({
          captureEnabled: tenant.settings.marketingCapture.captureEnabled ?? false,
          matchMode: tenant.settings.marketingCapture.matchMode || 'contains',
          targetEntity: legacyTargetEntity,
          dealPipelineId: legacyDealPipelineId,
          rules: normalizedRules,
        });
      }
    }
  }, [tenant?.settings]);

  useEffect(() => {
    const loadBitrixFields = async () => {
      if (!tenant?.id) return;
      try {
        const connections = await api.getConnections(tenant.id);
        const activeConnection =
          connections.find((connection) => connection.status === 'active') || connections[0];

        if (!activeConnection) {
          setBitrixFields(null);
          return;
        }

        const fields = await api.getBitrixFields(activeConnection.id);
        setBitrixFields(fields);
      } catch (error) {
        console.error('Failed to load Bitrix fields for marketing settings:', error);
        setBitrixFields(null);
      }
    };

    loadBitrixFields();
  }, [tenant?.id]);

  // Load API key statuses
  useEffect(() => {
    if (tenant?.id) {
      api.getOpenAIKeyStatus(tenant.id)
        .then(status => setOpenaiKeyStatus(status))
        .catch(() => { /* ignore */ });
      api.getGeminiKeyStatus(tenant.id)
        .then(status => setGeminiKeyStatus(status))
        .catch(() => { /* ignore */ });
      api.getAnthropicKeyStatus(tenant.id)
        .then(status => setAnthropicKeyStatus(status))
        .catch(() => { /* ignore */ });
    }
  }, [tenant?.id]);

  const tabs = [
    { id: 'general' as const, label: t('settings.tabs.general'), icon: SettingsIcon },
    { id: 'api' as const, label: t('settings.tabs.api'), icon: Key },
    { id: 'billing' as const, label: t('settings.tabs.billing'), icon: CreditCard },
    { id: 'notifications' as const, label: t('settings.tabs.notifications'), icon: Bell },
    { id: 'marketing' as const, label: t('settings.tabs.marketing'), icon: Target },
  ];

  const handleSave = async () => {
    if (isReadOnlyMode) return;
    const normalizedTechnicalEmail = technicalEmail.trim();
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (normalizedTechnicalEmail && !emailPattern.test(normalizedTechnicalEmail)) {
      toast.error(t('settings.toasts.invalidEmail'), t('settings.toasts.invalidEmailTitle'));
      return;
    }

    setIsSaving(true);
    try {
      // Update tenant settings
      if (tenant) {
        const settingsPatch = {
          notifications,
          marketingCapture: marketingSettings,
          technicalResponsibleEmail: normalizedTechnicalEmail,
        };

        console.log('Saving settings:', settingsPatch);
        
        await api.updateSettings(tenant.id, settingsPatch);

        const refreshedTenant = await api.getTenant(tenant.id);
        setTenant(refreshedTenant);
        setTechnicalEmail(
          refreshedTenant.settings?.technicalResponsibleEmail || refreshedTenant.email || '',
        );
        setIsTechnicalEmailDirty(false);
      }
      
      await new Promise((resolve) => setTimeout(resolve, 500));
      toast.success(t('settings.toasts.saveSuccess'), t('settings.toasts.savedTitle'));
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(t('settings.toasts.saveError'), t('settings.toasts.saveErrorTitle'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestNotification = async () => {
    if (isReadOnlyMode) return;
    if (!tenant) return;
    try {
      await api.sendTestNotification(tenant.id);
      toast.success(
        t('settings.toasts.testNotificationSuccess'),
        t('settings.toasts.testNotificationSuccessTitle'),
      );
    } catch (error) {
      toast.error((error as Error).message, t('settings.toasts.testNotificationErrorTitle'));
    }
  };

  const handleSaveOpenAIKey = async () => {
    if (isReadOnlyMode) return;
    if (!tenant || !openaiKey.trim()) return;
    if (!openaiKey.startsWith('sk-')) {
      toast.error(t('settings.toasts.invalidOpenAiKey'), t('settings.toasts.invalidOpenAiKeyTitle'));
      return;
    }
    setIsSavingKey(true);
    try {
      await api.saveOpenAIKey(tenant.id, openaiKey.trim());
      const status = await api.getOpenAIKeyStatus(tenant.id);
      setOpenaiKeyStatus(status);
      setOpenaiKey('');
      toast.success(t('settings.toasts.openAiKeySaved'), t('settings.toasts.savedTitle'));
    } catch (error) {
      toast.error((error as Error).message, t('settings.toasts.keyErrorTitle'));
    } finally {
      setIsSavingKey(false);
    }
  };

  const handleRemoveOpenAIKey = async () => {
    if (isReadOnlyMode) return;
    if (!tenant) return;
    setIsDeletingKey(true);
    try {
      await api.removeOpenAIKey(tenant.id);
      setOpenaiKeyStatus({ configured: false });
      setOpenaiKey('');
      toast.success(t('settings.toasts.openAiKeyRemoved'), t('settings.toasts.savedTitle'));
    } catch (error) {
      toast.error((error as Error).message, t('settings.toasts.keyRemoveErrorTitle'));
    } finally {
      setIsDeletingKey(false);
    }
  };

  const handleSaveGeminiKey = async () => {
    if (isReadOnlyMode) return;
    if (!tenant || !geminiKey.trim()) return;
    setIsSavingGeminiKey(true);
    try {
      await api.saveGeminiKey(tenant.id, geminiKey.trim());
      const status = await api.getGeminiKeyStatus(tenant.id);
      setGeminiKeyStatus(status);
      setGeminiKey('');
      toast.success(t('settings.toasts.geminiKeySaved'), t('settings.toasts.savedTitle'));
    } catch (error) {
      toast.error((error as Error).message, t('settings.toasts.keyErrorTitle'));
    } finally {
      setIsSavingGeminiKey(false);
    }
  };

  const handleRemoveGeminiKey = async () => {
    if (isReadOnlyMode) return;
    if (!tenant) return;
    setIsDeletingGeminiKey(true);
    try {
      await api.removeGeminiKey(tenant.id);
      setGeminiKeyStatus({ configured: false });
      setGeminiKey('');
      toast.success(t('settings.toasts.geminiKeyRemoved'), t('settings.toasts.savedTitle'));
    } catch (error) {
      toast.error((error as Error).message, t('settings.toasts.keyRemoveErrorTitle'));
    } finally {
      setIsDeletingGeminiKey(false);
    }
  };

  const handleSaveAnthropicKey = async () => {
    if (isReadOnlyMode) return;
    if (!tenant || !anthropicKey.trim()) return;
    if (!anthropicKey.startsWith('sk-ant-')) {
      toast.error('A chave do Claude deve começar com "sk-ant-".', t('settings.toasts.keyErrorTitle'));
      return;
    }
    setIsSavingAnthropicKey(true);
    try {
      await api.saveAnthropicKey(tenant.id, anthropicKey.trim());
      const status = await api.getAnthropicKeyStatus(tenant.id);
      setAnthropicKeyStatus(status);
      setAnthropicKey('');
      toast.success('Chave do Claude salva com sucesso.', t('settings.toasts.savedTitle'));
    } catch (error) {
      toast.error((error as Error).message, t('settings.toasts.keyErrorTitle'));
    } finally {
      setIsSavingAnthropicKey(false);
    }
  };

  const handleRemoveAnthropicKey = async () => {
    if (isReadOnlyMode) return;
    if (!tenant) return;
    setIsDeletingAnthropicKey(true);
    try {
      await api.removeAnthropicKey(tenant.id);
      setAnthropicKeyStatus({ configured: false });
      setAnthropicKey('');
      toast.success('Chave do Claude removida.', t('settings.toasts.savedTitle'));
    } catch (error) {
      toast.error((error as Error).message, t('settings.toasts.keyRemoveErrorTitle'));
    } finally {
      setIsDeletingAnthropicKey(false);
    }
  };

  const handleActivatePartner = async () => {
    if (isReadOnlyMode) return;
    if (!tenant?.id || !partnerCode.trim()) return;

    setIsActivatingPartner(true);
    try {
      const updatedTenant = await api.activatePartner(tenant.id, partnerCode.trim());
      setTenant(updatedTenant);
      setPartnerCode('');
      toast.success(t('settings.toasts.partnerActivated'), t('settings.toasts.partnerActivatedTitle'));
    } catch (error) {
      toast.error((error as Error).message, t('settings.toasts.invalidPartnerCodeTitle'));
    } finally {
      setIsActivatingPartner(false);
    }
  };

  const dealCategories = useMemo(() => {
    return Array.isArray(bitrixFields?.dealCategories) ? bitrixFields.dealCategories : [];
  }, [bitrixFields]);

  const updateMarketingRule = (index: number, key: string, value: string) => {
    const nextRules = [...(marketingSettings.rules || [])];
    nextRules[index] = {
      ...nextRules[index],
      [key]: value,
    };
    setMarketingSettings({ ...marketingSettings, rules: nextRules });
  };

  const addMarketingRule = () => {
    if (isReadOnlyMode) return;
    setMarketingSettings({
      ...marketingSettings,
      rules: [
        ...(marketingSettings.rules || []),
        {
          id: crypto.randomUUID(),
          code: '',
          targetEntity: 'lead',
          dealPipelineId: 'all',
          utmSource: '',
          utmMedium: '',
          utmCampaign: '',
          utmContent: '',
          utmTerm: '',
        },
      ],
    });
  };

  const removeMarketingRule = (index: number) => {
    if (isReadOnlyMode) return;
    const nextRules = [...(marketingSettings.rules || [])];
    nextRules.splice(index, 1);
    setMarketingSettings({ ...marketingSettings, rules: nextRules });
  };

  const currentLanguage = i18n.language.toLowerCase().startsWith('pt') ? 'pt-BR' : 'en';

  const handleChangeLanguage = async (language: 'pt-BR' | 'en') => {
    if (language === currentLanguage) {
      return;
    }

    setIsChangingLanguage(true);
    try {
      markLanguageAsUserSelected();
      await i18n.changeLanguage(language);
      toast.success(t('settings.toasts.languageUpdated'), t('settings.toasts.languageTitle'));
    } catch (error) {
      console.error('Failed to change language:', error);
      toast.error(t('settings.toasts.languageUpdateError'), t('settings.toasts.languageTitle'));
    } finally {
      setIsChangingLanguage(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{t('settings.general.accountTitle')}</CardTitle>
                <CardDescription>
                  {t('settings.general.accountDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Input
                  label={t('settings.general.companyName')}
                  value={tenant?.name || ''}
                  disabled
                />
                <Input
                  label={t('settings.general.technicalEmail')}
                  type="email"
                  value={technicalEmail}
                  onChange={(e) => {
                    setTechnicalEmail(e.target.value);
                    setIsTechnicalEmailDirty(true);
                  }}
                  placeholder={t('settings.general.technicalEmailPlaceholder')}
                  helperText={t('settings.general.technicalEmailHelper')}
                />
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {t('settings.general.accountStatus')}
                  </label>
                  <Badge
                    variant={
                      tenant?.status === 'active' ? 'success' :
                      tenant?.status === 'trial' ? 'info' :
                      tenant?.status === 'expired' ? 'danger' : 'warning'
                    }
                  >
                    {
                      tenant?.status === 'active' ? t('settings.general.statusActive') :
                      tenant?.status === 'trial' ? t('settings.general.statusTrial') :
                      tenant?.status === 'expired' ? t('settings.general.statusExpired') :
                      t('settings.general.statusPending')
                    }
                  </Badge>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                    {t('settings.general.languageLabel')}
                  </label>
                  <select
                    value={currentLanguage}
                    onChange={(event) => handleChangeLanguage(event.target.value as 'pt-BR' | 'en')}
                    disabled={isChangingLanguage}
                    className="w-full h-10 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-60"
                  >
                    <option value="pt-BR">{t('settings.general.languagePtBr')}</option>
                    <option value="en">{t('settings.general.languageEn')}</option>
                  </select>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    {t('settings.general.languageHelp')}
                  </p>
                </div>
                <Button onClick={handleSave} isLoading={isSaving}>
                  <Save className="w-4 h-4 mr-2" />
                  {t('settings.general.saveInfo')}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('settings.partner.title')}</CardTitle>
                <CardDescription>
                  {t('settings.partner.description')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {tenant?.isPartner ? (
                  <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3">
                    <p className="text-sm font-medium text-green-800">{t('settings.partner.alreadyPartner')}</p>
                  </div>
                ) : (
                  <>
                    <Input
                      label={t('settings.partner.codeLabel')}
                      value={partnerCode}
                      onChange={(event) => setPartnerCode(event.target.value)}
                      placeholder={t('settings.partner.codePlaceholder')}
                      helperText={t('settings.partner.codeHelp')}
                    />
                    <Button
                      onClick={handleActivatePartner}
                      isLoading={isActivatingPartner}
                      disabled={!partnerCode.trim()}
                    >
                      {t('settings.partner.activate')}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        );

      case 'api':
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{t('settings.api.openAiTitle')}</CardTitle>
                <CardDescription>
                  {t('settings.api.openAiDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {openaiKeyStatus.configured && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-green-800">{t('settings.api.keyConfigured')}</p>
                      <p className="text-xs text-green-600 font-mono">{openaiKeyStatus.maskedKey}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveOpenAIKey}
                      isLoading={isDeletingKey}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
                <Input
                  label={openaiKeyStatus.configured ? t('settings.api.replaceKey') : t('settings.api.apiKeyLabel')}
                  type="password"
                  placeholder="sk-..."
                  value={openaiKey}
                  onChange={(e) => setOpenaiKey(e.target.value)}
                  helperText={t('settings.api.keySecureHelp')}
                />
                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleSaveOpenAIKey}
                    isLoading={isSavingKey}
                    disabled={!openaiKey.trim()}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {openaiKeyStatus.configured ? t('settings.api.updateKey') : t('settings.api.saveKey')}
                  </Button>
                  <a
                    href="https://platform.openai.com/api-keys"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button variant="outline">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      {t('settings.api.getKey')}
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('settings.api.geminiTitle')}</CardTitle>
                <CardDescription>
                  {t('settings.api.geminiDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {geminiKeyStatus.configured && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-green-800">{t('settings.api.keyConfigured')}</p>
                      <p className="text-xs text-green-600 font-mono">{geminiKeyStatus.maskedKey}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveGeminiKey}
                      isLoading={isDeletingGeminiKey}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
                <Input
                  label={geminiKeyStatus.configured ? t('settings.api.replaceKey') : t('settings.api.apiKeyLabel')}
                  type="password"
                  placeholder="AIza..."
                  value={geminiKey}
                  onChange={(e) => setGeminiKey(e.target.value)}
                  helperText={t('settings.api.keySecureHelp')}
                />
                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleSaveGeminiKey}
                    isLoading={isSavingGeminiKey}
                    disabled={!geminiKey.trim()}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {geminiKeyStatus.configured ? t('settings.api.updateKey') : t('settings.api.saveKey')}
                  </Button>
                  <a
                    href="https://aistudio.google.com/apikey"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button variant="outline">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      {t('settings.api.getKey')}
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Claude (Anthropic)</CardTitle>
                <CardDescription>
                  Conecte sua chave da Anthropic para usar modelos Claude (Haiku, Sonnet, Opus) nos nós de IA.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {anthropicKeyStatus.configured && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-green-800">{t('settings.api.keyConfigured')}</p>
                      <p className="text-xs text-green-600 font-mono">{anthropicKeyStatus.maskedKey}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveAnthropicKey}
                      isLoading={isDeletingAnthropicKey}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
                <Input
                  label={anthropicKeyStatus.configured ? t('settings.api.replaceKey') : t('settings.api.apiKeyLabel')}
                  type="password"
                  placeholder="sk-ant-..."
                  value={anthropicKey}
                  onChange={(e) => setAnthropicKey(e.target.value)}
                  helperText={t('settings.api.keySecureHelp')}
                />
                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleSaveAnthropicKey}
                    isLoading={isSavingAnthropicKey}
                    disabled={!anthropicKey.trim()}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {anthropicKeyStatus.configured ? t('settings.api.updateKey') : t('settings.api.saveKey')}
                  </Button>
                  <a
                    href="https://console.anthropic.com/settings/keys"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button variant="outline">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      {t('settings.api.getKey')}
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>

          </div>
        );

      case 'billing':
        const trialDaysRemaining = tenant?.trialEndsAt 
          ? Math.ceil((new Date(tenant.trialEndsAt).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
          : 0;

        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{tenant?.isPartner ? t('settings.billing.partnerTitle') : t('settings.billing.title')}</CardTitle>
                <CardDescription>
                  {tenant?.isPartner ? t('settings.billing.partnerDescription') : t('settings.billing.description')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg mb-4">
                  <div>
                    <p className="font-semibold text-slate-900">
                      {tenant?.isPartner ? t('settings.billing.partnerPlan') : tenant?.subscriptionPlan?.toUpperCase() || 'FREE'}
                    </p>
                    <p className="text-sm text-slate-500">
                      {tenant?.isPartner ? t('settings.billing.partnerPlanHelper') : t('settings.billing.monthlyPlan')}
                    </p>
                  </div>
                  <Badge variant="info">
                    {tenant?.isPartner ? t('settings.billing.partnerBadge') : tenant?.subscriptionPlan?.toUpperCase() || 'FREE'}
                  </Badge>
                </div>

                {/* Trial Info */}
                {tenant?.status === 'trial' && (
                   <div className="mb-6 p-4 bg-blue-50 border border-blue-100 rounded-lg">
                      <h4 className="font-medium text-blue-900 mb-1">{t('settings.billing.trialTitle')}</h4>
                      <p className="text-sm text-blue-700">
                        {t('settings.billing.trialDescription', {
                          count: Math.max(0, trialDaysRemaining),
                        })}
                      </p>
                   </div>
                )}
              </CardContent>
            </Card>
          </div>
        );

      case 'notifications':
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{t('settings.notifications.title')}</CardTitle>
                <CardDescription>
                  {t('settings.notifications.description')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-slate-100 dark:border-slate-700">
                  <div>
                    <p className="font-medium text-slate-900 dark:text-slate-100">{t('settings.notifications.errorAlertsTitle')}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-300">{t('settings.notifications.errorAlertsDescription')}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleTestNotification}
                      disabled={!notifications.errorAlerts}
                    >
                      <Bell className="w-3 h-3 mr-2" />
                      {t('settings.notifications.testButton')}
                    </Button>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer" 
                        checked={notifications.errorAlerts}
                        onChange={(e) => setNotifications({...notifications, errorAlerts: e.target.checked})}
                      />
                      <div className="w-11 h-6 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 dark:after:border-slate-500 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>

                <div className="flex items-center justify-between py-3 border-b border-slate-100 dark:border-slate-700">
                  <div>
                    <p className="font-medium text-slate-900 dark:text-slate-100">{t('settings.notifications.connectionLostTitle')}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-300">{t('settings.notifications.connectionLostDescription')}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="sr-only peer" 
                      checked={notifications.connectionLost}
                      onChange={(e) => setNotifications({...notifications, connectionLost: e.target.checked})}
                    />
                    <div className="w-11 h-6 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 dark:after:border-slate-500 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between py-3 border-b border-slate-100 dark:border-slate-700 last:border-0">
                  <div>
                    <p className="font-medium text-slate-900 dark:text-slate-100">{t('settings.notifications.weeklyReportTitle')}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-300">{t('settings.notifications.weeklyReportDescription')}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="sr-only peer" 
                      checked={notifications.weeklyReport}
                      onChange={(e) => setNotifications({...notifications, weeklyReport: e.target.checked})}
                    />
                    <div className="w-11 h-6 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 dark:after:border-slate-500 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <Button onClick={handleSave} isLoading={isSaving}>
                  <Save className="w-4 h-4 mr-2" />
                  {t('settings.notifications.savePreferences')}
                </Button>
              </CardContent>
            </Card>
          </div>
        );
      case 'marketing':
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{t('settings.marketing.title')}</CardTitle>
                <CardDescription>
                  {t('settings.marketing.description')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/30 rounded-lg p-3 text-xs text-orange-700 dark:text-orange-200 leading-relaxed">
                  <strong>{t('settings.marketing.howItWorksTitle')}</strong> {t('settings.marketing.howItWorksBody')}
                  <br />
                  {t('settings.marketing.howItWorksFields')} <strong>UTM_SOURCE</strong>, <strong>UTM_MEDIUM</strong>, <strong>UTM_CAMPAIGN</strong>, <strong>UTM_CONTENT</strong> e <strong>UTM_TERM</strong>.
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between py-2 px-3 border border-slate-200 dark:border-slate-700 rounded-md md:col-span-2">
                    <div>
                      <label className="font-medium text-slate-900 dark:text-slate-100 block">{t('settings.marketing.enableTitle')}</label>
                      <p className="text-sm text-slate-500 dark:text-slate-300">{t('settings.marketing.enableDescription')}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={marketingSettings.captureEnabled}
                        onChange={(e) => setMarketingSettings({ ...marketingSettings, captureEnabled: e.target.checked })}
                      />
                      <div className="w-11 h-6 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-slate-500 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">{t('settings.marketing.matchType')}</label>
                    <select
                      className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                      value={marketingSettings.matchMode || 'contains'}
                      onChange={(e) =>
                        setMarketingSettings({
                          ...marketingSettings,
                          matchMode: e.target.value === 'exact' ? 'exact' : 'contains',
                        })
                      }
                      disabled={!marketingSettings.captureEnabled}
                    >
                      <option value="contains">{t('settings.marketing.matchContains')}</option>
                      <option value="exact">{t('settings.marketing.matchExact')}</option>
                    </select>
                  </div>

                </div>

                <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">{t('settings.marketing.rulesTitle')}</h4>
                    <Button variant="outline" onClick={addMarketingRule} disabled={!marketingSettings.captureEnabled}>
                      <Plus className="w-4 h-4 mr-2" />
                      {t('settings.marketing.addRule')}
                    </Button>
                  </div>

                  {(marketingSettings.rules || []).length === 0 && (
                    <p className="text-sm text-slate-500 dark:text-slate-300">{t('settings.marketing.noRules')}</p>
                  )}

                  <div className="space-y-3">
                    {(marketingSettings.rules || []).map((rule: any, index: number) => (
                      <div key={rule.id || index} className="border border-slate-200 dark:border-slate-700 rounded-md p-3 space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">{t('settings.marketing.targetEntity')}</label>
                            <select
                              className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                              value={rule.targetEntity === 'deal' ? 'deal' : 'lead'}
                              onChange={(e) => updateMarketingRule(index, 'targetEntity', e.target.value === 'deal' ? 'deal' : 'lead')}
                              disabled={!marketingSettings.captureEnabled}
                            >
                              <option value="lead">{t('settings.marketing.lead')}</option>
                              <option value="deal">{t('settings.marketing.deal')}</option>
                            </select>
                          </div>

                          <Input
                            label={t('settings.marketing.ruleCode')}
                            value={rule.code || ''}
                            onChange={(e) => updateMarketingRule(index, 'code', e.target.value)}
                            disabled={!marketingSettings.captureEnabled}
                          />

                          {rule.targetEntity === 'deal' && (
                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">{t('settings.marketing.dealPipelineLabel')}</label>
                              <select
                                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                                value={rule.dealPipelineId || 'all'}
                                onChange={(e) => updateMarketingRule(index, 'dealPipelineId', e.target.value)}
                                disabled={!marketingSettings.captureEnabled}
                              >
                                <option value="all">{t('settings.marketing.allPipelines')}</option>
                                {dealCategories.map((category: any) => (
                                  <option key={String(category.ID)} value={String(category.ID)}>
                                    {category.NAME || t('settings.marketing.pipelineFallback', { id: category.ID })}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}

                          <Input
                            label={t('settings.marketing.utmSource')}
                            value={rule.utmSource || ''}
                            onChange={(e) => updateMarketingRule(index, 'utmSource', e.target.value)}
                            disabled={!marketingSettings.captureEnabled}
                          />
                          <Input
                            label={t('settings.marketing.utmMedium')}
                            value={rule.utmMedium || ''}
                            onChange={(e) => updateMarketingRule(index, 'utmMedium', e.target.value)}
                            disabled={!marketingSettings.captureEnabled}
                          />
                          <Input
                            label={t('settings.marketing.utmCampaign')}
                            value={rule.utmCampaign || ''}
                            onChange={(e) => updateMarketingRule(index, 'utmCampaign', e.target.value)}
                            disabled={!marketingSettings.captureEnabled}
                          />
                          <Input
                            label={t('settings.marketing.utmContent')}
                            value={rule.utmContent || ''}
                            onChange={(e) => updateMarketingRule(index, 'utmContent', e.target.value)}
                            disabled={!marketingSettings.captureEnabled}
                          />
                          <Input
                            label={t('settings.marketing.utmTerm')}
                            value={rule.utmTerm || ''}
                            onChange={(e) => updateMarketingRule(index, 'utmTerm', e.target.value)}
                            disabled={!marketingSettings.captureEnabled}
                          />
                        </div>

                        <div className="flex justify-end">
                          <Button
                            variant="ghost"
                            onClick={() => removeMarketingRule(index)}
                            disabled={!marketingSettings.captureEnabled}
                            className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            {t('settings.marketing.removeRule')}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <Button onClick={handleSave} isLoading={isSaving}>
                  <Save className="w-4 h-4 mr-2" />
                  {t('settings.marketing.save')}
                </Button>
              </CardContent>
            </Card>
          </div>
        );
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24, ease: 'easeOut' }}
      className="space-y-6"
    >
      {isReadOnlyMode && (
        <Card className="bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30">
          <CardContent className="py-3 text-sm text-amber-900 dark:text-amber-200">
            {t('settings.page.readOnlyBanner')}
          </CardContent>
        </Card>
      )}

      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{t('settings.page.title')}</h1>
        <p className="text-slate-500 dark:text-slate-300 mt-1">
          {t('settings.page.subtitle')}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-slate-100 dark:bg-slate-800 rounded-lg w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm'
                : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className={isReadOnlyMode ? 'pointer-events-none opacity-60' : ''}
        >
          {renderTabContent()}
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
}
